import React from "react";
import { Button } from "@/components/ui/button";
import { LOAN_LEAD_VIEW_ROLES, PHOTO_VIEW_ROLES } from "@/lib/visitSchema";
import { BasicDetails, LoanLeadsTable, PhotoGroups, Restricted, Section, ValuationSection, VerificationSections } from "@/components/visit/summaryParts";
import { Pencil } from "lucide-react";

/* Derived counts/groupings for the read-only summary (server counts win when present). */
function summarize(visit) {
  const nb = visit.neighbor || {};
  const lr = visit.land_rate || {};
  const photos = visit.property_photos?.length ? visit.property_photos : (visit.photo_ids || []).map((id) => ({ file_id: id, tag: "other" }));
  const byTag = photos.reduce((acc, p) => ({ ...acc, [p.tag || "other"]: [...(acc[p.tag || "other"] || []), p] }), {});
  const verification = [["neighbor", nb.neighbor_photo_ids], ["local_body", nb.local_body_photo_ids], ["land_rate", lr.photo_ids], ["khasra", visit.khasra_photo_ids]]
    .map(([key, ids]) => ({ key, items: (ids || []).map((id) => ({ file_id: id })) }));
  const loanLeads = visit.loan_leads || [];
  return {
    byTag, verification,
    photoCount: visit.photo_count ?? photos.length,
    verificationCount: visit.verification_photo_count ?? verification.slice(0, 3).reduce((n, g) => n + g.items.length, 0),
    khasraCount: visit.khasra_doc_count ?? (visit.khasra_photo_ids || []).length,
    leadCount: visit.loan_leads_count ?? loanLeads.length,
    leadVerified: visit.loan_leads_verified ?? loanLeads.filter((l) => l.telecalling_verified).length,
  };
}

/* Read-only inspection summary. Photos render only for admin/office; everyone else sees counts. */
export default function VisitSummary({ visit, role, onEdit }) {
  const s = summarize(visit);
  const canViewPhotos = PHOTO_VIEW_ROLES.includes(role);
  const canViewLeads = LOAN_LEAD_VIEW_ROLES.includes(role);

  return (
    <div className="mt-6 border-t border-slate-200 pt-4" data-testid="visit-summary">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Inspection report</div>
        {onEdit && <Button type="button" size="sm" variant="outline" onClick={onEdit} data-testid="visit-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Update inspection</Button>}
      </div>
      <BasicDetails visit={visit} />
      <ValuationSection visit={visit} />
      <VerificationSections visit={visit} khasraCount={s.khasraCount} />
      <Section title={`Telecalling-confirmed loan leads · ${s.leadCount} on file (${s.leadVerified} verified)`} testId="summary-leads">
        {canViewLeads ? <LoanLeadsTable leads={visit.loan_leads || []} /> : <Restricted testId="summary-leads-restricted">Loan lead contacts are visible to the Admin role only.</Restricted>}
      </Section>
      <Section title={`Photos · ${s.photoCount} property · ${s.verificationCount} verification`} testId="summary-photos">
        {canViewPhotos ? <PhotoGroups byTag={s.byTag} verification={s.verification} /> : <Restricted testId="summary-photos-restricted">Photo uploads are visible to Admin and Office Staff only.</Restricted>}
      </Section>
    </div>
  );
}
